import React, { useEffect, useState } from "react";
import { orderService } from "../features/orders/orderService";
import { AvailableTimeSlot } from "../features/orders/types";
import { formatDayLabel, formatSlotTime, nextDays } from "../utils/orderFormat";

export type SlotSelection = { slotId: string; date: string } | null;

interface TimeSlotPickerProps {
  type: "pickup" | "delivery";
  value: SlotSelection;
  onChange: (value: SlotSelection) => void;
  days?: number;
}

export const TimeSlotPicker: React.FC<TimeSlotPickerProps> = ({
  type,
  value,
  onChange,
  days = 7,
}) => {
  const dates = nextDays(days);
  const [date, setDate] = useState(value?.date ?? dates[0]);
  const [slots, setSlots] = useState<AvailableTimeSlot[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    orderService
      .getAvailableTimeSlots(type, date)
      .then((result) => {
        if (!cancelled) setSlots(result);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load time slots");
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [type, date]);

  return (
    <div className="space-y-3">
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
        {dates.map((d) => (
          <button
            key={d}
            type="button"
            onClick={() => {
              setDate(d);
              if (value?.date !== d) onChange(null);
            }}
            className={`flex-shrink-0 rounded-xl px-3 py-2 text-xs font-semibold transition-all ${
              d === date
                ? "bg-[#44B6CE] text-white shadow-sm"
                : "bg-white border border-gray-100 text-gray-600"
            }`}
          >
            {formatDayLabel(d)}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-4">
          <div className="w-6 h-6 border-4 border-[#44B6CE] border-t-transparent animate-spin rounded-full" />
        </div>
      ) : error ? (
        <p className="text-xs text-red-500">{error}</p>
      ) : slots.length === 0 ? (
        <p className="text-xs text-gray-400 bg-white rounded-xl border border-gray-100 px-4 py-3">
          No time slots available for this day.
        </p>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          {slots.map(({ slot, available, remaining }) => {
            const selected = value?.slotId === slot.id && value?.date === date;
            return (
              <button
                key={slot.id}
                type="button"
                disabled={!available}
                onClick={() => onChange({ slotId: slot.id, date })}
                className={`rounded-xl px-3 py-3 text-left transition-all ${
                  selected
                    ? "bg-[#44B6CE]/10 border-2 border-[#44B6CE]"
                    : "bg-white border border-gray-100"
                } ${!available ? "opacity-40" : ""}`}
              >
                <p className="text-sm font-bold text-gray-800">{slot.label}</p>
                <p className="text-xs text-gray-500">{formatSlotTime(slot)}</p>
                {!available ? (
                  <p className="text-[10px] font-semibold text-gray-400 mt-1">Unavailable</p>
                ) : remaining !== null && remaining <= 3 ? (
                  <p className="text-[10px] font-semibold text-orange-500 mt-1">
                    {remaining} left
                  </p>
                ) : null}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
